import React, { useEffect, useMemo, useState } from "react";
import { cents, modules } from "byzantine";
import filters from "byzantine/src/filters";
import {
  Table,
  Checkbox,
  TableInput,
  TableSelect,
  Chip,
  IconName,
} from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import { InstitutionFeatures } from "byzantine/src/types";
import { MAX_MEMO_LENGTH } from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import { BulkTemplatePaymentRowType } from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates";
import * as formAdapters from "../../../../../src/adapters";
import { useUserFeatures } from "../../../../contexts/UserFeaturesContext";
import styles from "../../../../PaymentActivity/CreateBulk/table/BulkPaymentTable.module.scss";
import {
  SelectedRecipientCard,
  RecipientSelector,
  getRecipientError,
  getAmountError,
} from "../../../../PaymentActivity/CreateBulk/table";

type PaymentsTableRowType = {
  payment: BulkTemplatePaymentRowType;
  index: number;
  form: any;
  recipients: API.Recipient[];
  selectedRows: number[];
  toggleRowSelection: (index: number) => void;
  handleClickEditRecipient: (
    recipientId: API.RecipientId,
    rowIndex?: number
  ) => void;
  handleClickAddRecipient: (rowIndex: number) => void;
  setCustomAlertCopy: (copy: string) => void;
  showSpeedColumn: boolean;
  eligibilityCache: Record<string, boolean>;
  // table-level direction framing, so every row signs its amount consistently
  showCollectAmounts: boolean;
};

const TEMPLATE_SPEED_CONFIG = {
  instant: {
    label: "Instant available",
    icon: "zap" as IconName,
  },
  sameDay: {
    label: "Same-day available",
    icon: "clock" as IconName,
  },
  standard: {
    label: "Sending standard",
    icon: "calendar" as IconName,
  },
};

const usePaymentSpeedStatus = (
  features: InstitutionFeatures,
  payment: BulkTemplatePaymentRowType,
  eligibilityCache: Record<string, boolean>
) => {
  const { checkReceiveInstantEligibility, loading } =
    modules.transfers.useCheckReceiveInstantEligibility();

  const [speedStatus, setSpeedStatus] = useState<
    "instant" | "sameDay" | "standard" | ""
  >("");

  const {
    fednow_send: fedNowFeature,
    same_day_ach: sameDayFeature,
    expedited_payments: expeditedPaymentsFeature,
  } = features;
  const hasRecipient = Boolean(payment.recipient?.id);
  const routingNumber = payment.recipient?.ach_destination?.routing_number;
  const { transactionType } = payment;

  useEffect(() => {
    if (!hasRecipient || !routingNumber || !expeditedPaymentsFeature) {
      setSpeedStatus("");
      return;
    }

    const sameDayOptionOnly = sameDayFeature && !fedNowFeature;
    const fedNowOptionOnly = fedNowFeature && !sameDayFeature;
    const sameDayAndFedNowOptionsOnly = sameDayFeature && fedNowFeature;

    const isCollectPayment = transactionType === "collect";
    const isSendPayment = transactionType === "send";

    const determineStatus = async () => {
      let status: "instant" | "sameDay" | "standard" | "" = "standard";

      const fetchEligibility = async () => {
        if (eligibilityCache[routingNumber] !== undefined) {
          return eligibilityCache[routingNumber];
        }
        const eligible = await checkReceiveInstantEligibility(routingNumber);
        eligibilityCache[routingNumber] = eligible;
        return eligible;
      };

      if (fedNowOptionOnly) {
        if (isSendPayment) {
          const eligible = await fetchEligibility();
          status = eligible ? "instant" : "standard";
        } else if (isCollectPayment) {
          status = "standard";
        }
      } else if (sameDayOptionOnly) {
        status = "sameDay";
      } else if (sameDayAndFedNowOptionsOnly) {
        if (isSendPayment) {
          const eligible = await fetchEligibility();
          status = eligible ? "instant" : "sameDay";
        } else if (isCollectPayment) {
          status = "sameDay";
        }
      }

      setSpeedStatus(status);
    };

    determineStatus();
  }, [
    hasRecipient,
    routingNumber,
    fedNowFeature,
    sameDayFeature,
    transactionType,
    expeditedPaymentsFeature,
  ]);

  return { speedStatus, loading };
};

const PaymentsTableRow = React.memo(
  ({
    payment,
    index,
    form,
    recipients,
    selectedRows,
    toggleRowSelection,
    handleClickEditRecipient,
    handleClickAddRecipient,
    setCustomAlertCopy,
    showSpeedColumn,
    eligibilityCache,
    showCollectAmounts,
  }: PaymentsTableRowType) => {
    const { l10n } = useLocalization();
    const features = useUserFeatures();
    const { ach_payments_collect: paymentsCollectFeature } = features;

    const { speedStatus, loading: eligibilityLoading } = usePaymentSpeedStatus(
      features,
      payment,
      eligibilityCache
    );

    useEffect(() => {
      if (!showSpeedColumn) return;
      const current = form.values.payments[index]?.speedStatus;
      if (payment.recipient?.id && current !== speedStatus) {
        form.setFieldValue(`payments.${index}.speedStatus`, speedStatus);
      }
    }, [speedStatus, index, form, payment.recipient?.id, showSpeedColumn]);

    const SpeedChip = useMemo(() => {
      if (!payment.recipient?.id) return null;
      if (eligibilityLoading) {
        return <Chip kind="info" startIcon="loader" label="" />;
      }
      const config = speedStatus
        ? TEMPLATE_SPEED_CONFIG[speedStatus]
        : TEMPLATE_SPEED_CONFIG.standard;
      return <Chip kind="info" label={config.label} startIcon={config.icon} />;
    }, [eligibilityLoading, speedStatus, payment.recipient?.id]);

    const recipientError = getRecipientError(form, index);
    const amountError = getAmountError(form, index);
    const { transactionType } = payment;
    const isCollectPayment = transactionType === "collect";
    const isSendPayment = transactionType === "send";

    const customDisplayValue = useMemo(() => {
      if (!showCollectAmounts) {
        return undefined;
      }

      const value = filters.currency(cents(payment.amount));

      if (isCollectPayment) {
        return `+${value}`;
      }

      if (isSendPayment) {
        return `-${value}`;
      }

      return undefined;
    }, [showCollectAmounts, isCollectPayment, isSendPayment, payment.amount]);

    const amountInputProps = formAdapters.useAmountInput(
      form,
      `payments.${index}.amount`,
      customDisplayValue
    );

    return (
      <Table.Row key={`table-row__${index}`}>
        <Table.Cell>
          <Checkbox
            onChange={() => toggleRowSelection(index)}
            checked={selectedRows.includes(index)}
          />
        </Table.Cell>
        <Table.Cell>
          {payment.recipient?.id ? (
            <SelectedRecipientCard
              recipientId={payment.recipient?.id}
              rowIndex={index}
              form={form}
              recipients={recipients}
              handleClickEditRecipient={handleClickEditRecipient}
            />
          ) : (
            <RecipientSelector
              rowIndex={index}
              recipients={recipients}
              form={form}
              handleClickAddRecipient={handleClickAddRecipient}
              handleClickEditRecipient={handleClickEditRecipient}
              setCustomAlertCopy={setCustomAlertCopy}
              error={Boolean(recipientError)}
            />
          )}
        </Table.Cell>
        <Table.Cell>
          {paymentsCollectFeature ? (
            <TableSelect
              label={l10n.getString("label-action")}
              {...formAdapters.select(
                form,
                `payments.${index}.transactionType`
              )}
            >
              <TableSelect.Item value={"send"}>
                {l10n.getString("send-label")}
              </TableSelect.Item>
              <TableSelect.Item value={"collect"}>
                {l10n.getString("collect-label")}
              </TableSelect.Item>
            </TableSelect>
          ) : (
            <span className={styles.capitalize}>{transactionType}</span>
          )}
        </Table.Cell>
        {showSpeedColumn && <Table.Cell>{SpeedChip}</Table.Cell>}
        <Table.Cell>
          <TableInput
            maxLength={MAX_MEMO_LENGTH}
            label={l10n.getString("label-enter-description")}
            placeholder={l10n.getString("label-enter-description")}
            {...formAdapters.input(form, `payments.${index}.description`)}
          />
        </Table.Cell>
        <Table.Cell>
          <div className={styles.rightAligned}>
            <TableInput
              label={l10n.getString("label-amount")}
              placeholder={l10n.getString("label-amount")}
              {...amountInputProps}
              hasError={Boolean(amountError)}
            />
          </div>
        </Table.Cell>
      </Table.Row>
    );
  }
);

export default PaymentsTableRow;
